import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../../services/adminService';

type Provider = 'smtp' | 'resend' | 'sendgrid';

interface EmailConfig {
  sender?: string;
  provider?: Provider;
  enabled?: boolean;
  templates?: Record<string, string>;
}

const EmailIntegration: React.FC = () => {
  const [config, setConfig] = useState<EmailConfig>({ enabled: false, provider: 'smtp', templates: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await adminService.getEmailConfig();
        if (mounted) setConfig({
          enabled: !!data?.enabled,
          provider: (data?.provider as Provider) || 'smtp',
          sender: data?.sender || '',
          templates: data?.templates || {},
        });
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load Email configuration');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    if (!config.sender) { toast.error('Sender is required'); return; }
    setSaving(true);
    try {
      await adminService.saveEmailConfig(config);
      toast.success('Email configuration saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save Email configuration');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setSending(true);
    try {
      const res = await adminService.sendEmailTest();
      if (res?.delivered) toast.success('Test email delivered'); else toast('Test sent');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send test email');
    } finally { setSending(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading Email settings…</div>;

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Email Notifications</h3>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!config.enabled} onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))} />
          <span>Enabled</span>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="email-sender" className="block text-xs text-gray-600 mb-1">Sender</label>
          <input id="email-sender" className="border rounded px-3 py-2 w-full" value={config.sender || ''} onChange={(e) => setConfig((c) => ({ ...c, sender: e.target.value }))} placeholder="no-reply@yourdomain.com" />
        </div>
        <div>
          <label htmlFor="email-provider" className="block text-xs text-gray-600 mb-1">Provider</label>
          <select id="email-provider" className="border rounded px-3 py-2 w-full" value={config.provider} onChange={(e) => setConfig((c) => ({ ...c, provider: e.target.value as Provider }))}>
            <option value="smtp">SMTP</option>
            <option value="resend">Resend</option>
            <option value="sendgrid">SendGrid</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Templates</label>
          <div className="text-xs text-gray-500">Configure templates server-side; UI editing coming soon.</div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={handleTest} disabled={sending} className="px-3 py-2 rounded border disabled:opacity-60">{sending ? 'Sending…' : 'Send Test'}</button>
      </div>
    </div>
  );
};

export default EmailIntegration;
